import { useEffect, useRef } from 'react';

import { getAttribution } from '../attribution';
import { useAnalyticsEvents } from '../generated/use-analytics-events';
import type { MicrositeTier, TierResolution } from '../types';

export type UseTierShownEventArgs = {
	/**
	 * Resolved tier for this page load. `undefined` while still resolving;
	 * passing `undefined` is a no-op and lets callers wire the hook
	 * unconditionally even before metric fetches resolve.
	 */
	tier: MicrositeTier | undefined;
	/**
	 * How the tier was decided. Required when `tier` is defined; ignored
	 * when `tier` is `undefined`.
	 */
	resolution: TierResolution;
	/** Whether the user-metrics payload was non-null when the tier resolved. */
	hasUserMetrics: boolean;
	/** Whether the org-metrics payload was non-null when the tier resolved. */
	hasOrgMetrics: boolean;
	/**
	 * Number of metric cards rendered immediately after tier resolution,
	 * including loading skeletons but excluding error states. `0` for
	 * `loggedOut` and (typically) `gettingStarted` tiers.
	 */
	metricCount: number;
};

/**
 * Fires `track.twgMicrositeTier.shown` once per resolved
 * `(tier, resolution)` tuple per page load.
 *
 * Re-fires when a late-resolving fetch flips the tier (for example,
 * user-metrics arrive after first paint and elevate the user from
 * `gettingStarted` to `userGraph`); each unique tuple fires at most
 * once per page load. The dedupe set is keyed on the literal
 * `${tier}:${resolution}` string so an organic-vs-debug-override flip
 * for the same tier still emits a second event.
 *
 * Skipping when `tier === undefined` lets callers wire the hook
 * unconditionally before metrics resolve, without spurious early
 * fires.
 */
export const useTierShownEvent = ({
	tier,
	resolution,
	hasUserMetrics,
	hasOrgMetrics,
	metricCount,
}: UseTierShownEventArgs): void => {
	const { fireEvent } = useAnalyticsEvents();
	const seenTuplesRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (tier === undefined) {
			return;
		}
		const key = `${tier}:${resolution}`;
		if (seenTuplesRef.current.has(key)) {
			return;
		}
		seenTuplesRef.current.add(key);
		fireEvent('track.twgMicrositeTier.shown', {
			tier,
			resolution,
			hasUserMetrics,
			hasOrgMetrics,
			metricCount,
			...getAttribution(),
		});
		// `fireEvent` is stable per-mount (returned by `useAnalyticsEvents`)
		// and `getAttribution` reads from a module-level const, so neither
		// needs to be in the deps array. Including them would not change
		// behaviour but would clutter the lint surface.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tier, resolution, hasUserMetrics, hasOrgMetrics, metricCount]);
};
